/**
 * Inventory of ZEXP / Split.io experiments active in hotpads-web-next.
 *
 * Add a new trial by creating it in ZEXP first, then adding an entry here. The
 * literal key becomes part of the `ExperimentName` union, so every consumer
 * (`useTreatment`, `prefetchTreatment`) gets typo-safety against this list.
 *
 * When a trial ends, delete its entry — TypeScript will surface the leftover
 * call sites for cleanup.
 */

type ExperimentEntry = {
  jira: string;
  owner: string;
  expiresAt?: string;
};

export const EXPERIMENTS = {
  HOTPADS_WEB_NEXT_REWRITE: { jira: 'HP-8182', owner: 'rentals-growth', expiresAt: '2027-06-25' },
} as const satisfies Record<string, ExperimentEntry>;

export type ExperimentName = keyof typeof EXPERIMENTS;

/**
 * Regex-based format check. We deliberately gate URL-override persistence on
 * the wire format (UPPER_SNAKE, 2–64 chars), NOT on registry membership:
 *
 * - When `EXPERIMENTS` is empty (the day-1 state of this module), a strict
 *   membership check would reject every override and the QA flow couldn't
 *   exercise an experiment until the registry entry merged.
 * - The registry is a compile-time typo guard for typed callers
 *   (`useTreatment(name: ExperimentName)`); it doesn't need to gate runtime.
 *
 * `isRegisteredExperiment` is exposed separately for callers that want the
 * stricter check (e.g. a future dev panel that lists known experiments).
 */
const NAME_FORMAT = /^[A-Z][A-Z0-9_]{1,63}$/;

export function isExperimentName(value: string): value is ExperimentName {
  return NAME_FORMAT.test(value);
}

export function isRegisteredExperiment(value: string): value is ExperimentName {
  return Object.prototype.hasOwnProperty.call(EXPERIMENTS, value);
}
